import { useEffect, useRef, useState } from "react";

type UseSmoothLoadingOptions = {
  loading: boolean;
  showDelayMs: number;
  minVisibleMs: number;
  /** When true, skip delayed/min-visible shimmer (previous data is still on screen). */
  hasPreviousData?: boolean;
};

/**
 * Softens loader flicker for subsequent fetches. The first loading cycle shows
 * immediately so refresh does not flash empty content before the shimmer.
 * When `hasPreviousData` is set, never force a min-visible blank shimmer.
 */
export const useSmoothLoading = ({
  loading,
  showDelayMs,
  minVisibleMs,
  hasPreviousData = false,
}: UseSmoothLoadingOptions) => {
  const [smoothLoading, setSmoothLoading] = useState(loading && !hasPreviousData);
  const shownAtRef = useRef<number | null>(loading && !hasPreviousData ? Date.now() : null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCompletedFirstCycleRef = useRef(false);

  useEffect(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (loading) {
      if (hasPreviousData) {
        setSmoothLoading(false);
        shownAtRef.current = null;
        return;
      }
      const delay = hasCompletedFirstCycleRef.current ? showDelayMs : 0;
      showTimerRef.current = setTimeout(() => {
        setSmoothLoading(true);
        shownAtRef.current = Date.now();
      }, delay);
      return;
    }

    if (!smoothLoading) {
      hasCompletedFirstCycleRef.current = true;
      return;
    }

    const shownAt = shownAtRef.current ?? Date.now();
    const elapsed = Date.now() - shownAt;
    const remaining = hasPreviousData ? 0 : Math.max(0, minVisibleMs - elapsed);

    hideTimerRef.current = setTimeout(() => {
      setSmoothLoading(false);
      shownAtRef.current = null;
      hasCompletedFirstCycleRef.current = true;
    }, remaining);
  }, [loading, smoothLoading, minVisibleMs, showDelayMs, hasPreviousData]);

  useEffect(
    () => () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    [],
  );

  return smoothLoading;
};
